import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full transition-transform duration-150', {
  variants: {
    size: {
      default: 'size-10', // 40px
      xs: 'size-6', // 24px
      sm: 'size-8', // 32px
      md: 'size-16', // 64px
      lg: 'size-32 [&_span]:text-3xl', // 128px
      xl: 'size-40 [&_span]:text-3xl' // 160px
    },
    isHoverable: {
      true: 'hover:relative hover:z-10 hover:-translate-y-1'
    }
  },
  defaultVariants: {
    size: 'default',
    isHoverable: false
  }
});

const Avatar = React.forwardRef(({ size, className, isHoverable = false, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn('c-avatar', avatarVariants({ size, isHoverable }), className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn('flex h-full w-full cursor-default items-center justify-center rounded-full bg-primary-lightest text-primary-dark', className)}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

const AvatarWrapper = React.forwardRef(({ className, src, text, size, isHoverable = false, ...props }, ref) => {
  function getInitialsFromFullName(fullName) {
    if (!fullName) {
      return '';
    }

    const nameParts = fullName.split(' ').filter(word => word.length > 0);

    if (nameParts.length === 0) {
      return '';
    }

    const firstInitial = nameParts[0].charAt(0).toUpperCase();

    if (nameParts.length > 1) {
      const secondInitial = nameParts[1].charAt(0).toUpperCase();
      return firstInitial + secondInitial;
    }

    return firstInitial;
  }

  return (
    <Avatar
      ref={ref}
      size={size}
      isHoverable={isHoverable}
      className={cn('c-avatar-wrapper', className)}
      {...props}
    >
      <AvatarImage
        src={src}
        alt={text}
      />
      <AvatarFallback className="cursor-[inherit]">{getInitialsFromFullName(text)}</AvatarFallback>
    </Avatar>
  );
});
AvatarWrapper.displayName = 'AvatarWrapper';

const AvatarStack = React.forwardRef(({ className, size, ...props }, ref) => {
  const getSpaceX = size => {
    switch (size) {
      case 'xs':
        return '-space-x-1';
      case 'sm':
        return '-space-x-2';
      case 'md':
        return '-space-x-6';
      case 'lg':
        return '-space-x-8';
      case 'xl':
        return '-space-x-10';
      default:
        return '-space-x-2';
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        'c-avatar-stack',
        // styles
        'flex',
        getSpaceX(size),
        className
      )}
      {...props}
    />
  );
});
AvatarStack.displayName = 'AvatarStack';

export { Avatar, AvatarImage, AvatarFallback, AvatarWrapper, AvatarStack };
